import React, { useCallback, useMemo, useRef, useState } from 'react';
import { MapCombo } from '@/src/components/feature/GoogleMap';
import { GetStaticPaths, GetStaticProps } from 'next';
import FunwooAPI, { BuildingShowcaseDetailsEntity, TrafficEnvCategory } from '@/swagger';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import FullPage from '@/src/components/UI/FullPage';
import OpenGraph from '@/components/OpenGraph';
import GoogleMapReact from 'google-map-react';

interface Props {
  showcase: BuildingShowcaseDetailsEntity;
}

const Map: React.FC<Props> = ({
  showcase: { latitude, longitude, adTitle, sid, featureDescription, buildingTrafficEnv, buildingImage },
}) => {
  const defaultCenter = useRef({ lat: latitude, lng: longitude });

  const [center, setCenter] = useState<GoogleMapReact.Coords | null>(defaultCenter.current);
  const [selectedCategory, setSelectedCategory] = useState<TrafficEnvCategory>(TrafficEnvCategory.COMMUNICATION);
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);

  const onCategoryClick = useCallback<React.MouseEventHandler<HTMLButtonElement>>((event) => {
    const key = event.currentTarget.dataset.key as TrafficEnvCategory;
    setSelectedCategory(key);
  }, []);

  const selectedCategoryItem = useMemo(
    () => buildingTrafficEnv.filter((item) => item.category === selectedCategory),
    [selectedCategory, buildingTrafficEnv]
  );

  return (
    <React.Fragment>
      <OpenGraph
        title={`FUNWOO 德載國際不動產 - ${adTitle}`}
        type='funwoo.building'
        url={`https://funwoo.com.tw/building/${sid}`}
        imageUrl={buildingImage[0]?.watermark ?? buildingImage[0]?.original ?? ''}
        description={featureDescription}
      />
      <FullPage
        title={adTitle}
        hasPadding
        hasBottomMargin={false}
        hasHeaderPlaceHolder
        headerRight={['share']}
        shareSocialInfo={{
          title: adTitle,
          url: `https://funwoo.com.tw/building/${sid}`,
          text: featureDescription,
        }}
        frameProps={{
          className: 'flex flex-col flex-1 w-full',
        }}
        mobileFlexProps={{
          id: 'mobile-content',
          className: 'flex flex-col flex-1 w-full',
        }}
        appContentProps={{
          w: 'full',
          h: 'full',
        }}
      >
        <div className={'-mx-4 h-full'}>
          <div className={'w-full h-full'}>
            <MapCombo
              selectedItemId={selectedItemId}
              setSelectedItemId={setSelectedItemId}
              defaultCenter={defaultCenter.current}
              center={center}
              setCenter={setCenter}
              selectedCategory={selectedCategory}
              items={selectedCategoryItem}
              streetViewUrl={`/building/${sid}/street`}
            />
          </div>
        </div>
      </FullPage>
    </React.Fragment>
  );
};

export default Map;

export const getStaticPaths: GetStaticPaths<{ sid: string }> = async () => {
  const { data: sids } = await FunwooAPI.buildingApi.getBuildingSids();

  return {
    paths: sids.map((sid) => ({
      params: {
        sid,
      },
    })),
    fallback: 'blocking',
  };
};

export const getStaticProps: GetStaticProps<Props> = async ({ locale = '', params = { entity: [] } }) => {
  try {
    const sid = params.sid as string;
    const { data } = await FunwooAPI.buildingApi.getShowcaseDetails(sid);
    return {
      props: {
        ...(await serverSideTranslations(locale, ['common'])),
        showcase: data,
      },
      revalidate: Number(process.env.NEXT_PUBLIC_REVALIDATE_INTERVAL),
    };
  } catch (e) {
    return {
      notFound: true,
    };
  }
};
